import { mutationOptions, UseMutationOptions } from '@tanstack/react-query';
import { BaseApiResponse } from './Api';
import { apiQueryOptions, BaseApiQueryOptions } from './apiQueryOptions';
import { apiOf } from './runtime/binding';

/**
 * The export endpoint answers with the workbook rather than the ApiResponse envelope, so its
 * failures arrive as a blob holding the envelope's JSON. Read it back, otherwise the screen shows
 * a generic error for a request that said exactly what was wrong.
 */
const toApiError = async (error: any): Promise<BaseApiResponse> => {
    if (error?.error instanceof Blob) {
        try {
            return JSON.parse(await error.error.text()) as BaseApiResponse;
        } catch {
            // Not the envelope after all — fall through to the generic shape below.
        }
    }

    return {
        success: false,
        statusCode: error?.status || 500,
        message: error?.statusText || 'An unknown error occurred'
    } as BaseApiResponse;
};

export const UsageStatisticsApi = {
    range: (from: string, to: string, options?: BaseApiQueryOptions) =>
        apiQueryOptions(['usage-statistics', 'range', from, to], (api) => api.usageStatistics.range(from, to), options),
    daily: (from: string, to: string, options?: BaseApiQueryOptions) =>
        apiQueryOptions(['usage-statistics', 'daily', from, to], (api) => api.usageStatistics.daily(from, to), options),
    billing: (from: string, to: string, options?: BaseApiQueryOptions) =>
        apiQueryOptions(['usage-statistics', 'billing', from, to], (api) => api.usageStatistics.billing(from, to), options),

    // Today changes while the day runs, so it must not be served from a stale cache the way a
    // frozen range can be.
    today: (options?: BaseApiQueryOptions) =>
        apiQueryOptions(['usage-statistics', 'today'], (api) => api.usageStatistics.today(), { staleTime: 60_000, ...options }),

    /**
     * The export is fetched, not linked. Dashboard auth is JWT Bearer and the token is attached as
     * a header by the client's securityWorker; a plain `<a href>` navigation sends no such header,
     * so the endpoint would 401 and the browser would land the user on the error body. Putting the
     * token in the query string instead would write it into browser history and server logs.
     *
     * So the workbook comes back as a blob and is handed to the browser through an object URL and
     * a synthetic download link — the same route EditSubtitlesPage takes for its VTT download.
     */
    exportRange: (options?: Partial<UseMutationOptions<void, BaseApiResponse, { from: string; to: string }>>) =>
        mutationOptions<void, BaseApiResponse, { from: string; to: string }>({
            mutationFn: async ({ from, to }, context) => {
                let workbook: Blob;
                try {
                    workbook = (await apiOf(context.client).usageStatistics.export(from, to)).data;
                } catch (error: any) {
                    throw await toApiError(error);
                }

                const url = URL.createObjectURL(workbook);
                try {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `usage-statistics-${from}-to-${to}.xlsx`;
                    link.click();
                } finally {
                    URL.revokeObjectURL(url);
                }
            },
            ...options
        })
};
